import { useMemo, useState } from 'react';
import { createCalendarProject } from '../lib/createCalendarProject';
import { saveProject } from '../lib/projectStorage';
import type { CalendarPeriodMode, CalendarProductId, CalendarThemeId } from '../types/calendarProject';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

const products: Array<{
  id: CalendarProductId;
  title: string;
  description: string;
  format: string;
  audience: string;
  badge: string;
  image: string;
}> = [
  {
    id: 'basic_year_story',
    title: 'Basic Year Story',
    description: 'Rychlý nástěnný kalendář z nejkrásnějších momentů roku.',
    format: 'A3 nástěnný',
    audience: 'rychlý osobní nebo dárkový kalendář',
    badge: 'Nejrychlejší start',
    image: 'product_basic_year_story',
  },
  {
    id: 'family_planner_cz',
    title: 'Family Planner CZ',
    description: 'Rodinný plánovací kalendář s českými svátky, jmeninami a poznámkami.',
    format: 'A3/A4 plánovací',
    audience: 'rodiny, domácnosti, prarodiče',
    badge: 'Pro rodinu',
    image: 'product_family_planner_cz',
  },
  {
    id: 'fineart_gallery',
    title: 'FineArt Gallery',
    description: 'Prémiový fotografický kalendář ve stylu galerie na zdi.',
    format: 'A3/A2 FineArt',
    audience: 'fotografie, cestování, prémiový dárek',
    badge: 'Prémiový dárek',
    image: 'product_fineart_gallery',
  },
];

const periodModes: Array<{ id: CalendarPeriodMode; title: string; description: string }> = [
  { id: 'calendar_year', title: 'Kalendářní rok 2026', description: 'Klasický leden až prosinec.' },
  { id: 'custom_start', title: 'Od libovolného měsíce', description: 'Vyber měsíc a rok začátku.' },
  { id: 'school_year', title: 'Školní rok září–srpen', description: 'Ideální pro rodiny a školní plánování.' },
  { id: 'anniversary_year', title: 'Výroční / vlastní rok', description: 'Rok od narozenin, svatby nebo jiné události.' },
];

const themes: Array<{ id: CalendarThemeId; title: string; description: string }> = [
  { id: 'clean_gallery', title: 'Clean Gallery', description: 'minimalistický, světlý, velká fotka' },
  { id: 'warm_family', title: 'Warm Family', description: 'měkčí rodinný styl, více prostoru pro poznámky' },
  { id: 'premium_blue', title: 'Premium Blue', description: 'elegantní navy/blue styl podle Calendar Studio' },
];

const months = [
  'leden',
  'únor',
  'březen',
  'duben',
  'květen',
  'červen',
  'červenec',
  'srpen',
  'září',
  'říjen',
  'listopad',
  'prosinec',
];

const validProductIds = products.map((product) => product.id);
const stepTitles = ['Produkt', 'Období', 'Styl', 'Shrnutí'];

function getInitialProductId(): CalendarProductId {
  if (typeof window === 'undefined') return 'basic_year_story';

  const productId = new URLSearchParams(window.location.search).get('product') as CalendarProductId | null;
  return productId && validProductIds.includes(productId) ? productId : 'basic_year_story';
}

function productImagePath(image: string, extension: 'webp' | 'jpg') {
  return `/assets/homepage/products/${image}.${extension}`;
}

export function StartFlow() {
  const [currentStep, setCurrentStep] = useState(0);
  const [productId, setProductId] = useState<CalendarProductId>(getInitialProductId);
  const [periodMode, setPeriodMode] = useState<CalendarPeriodMode>('calendar_year');
  const [year, setYear] = useState(2026);
  const [startMonth, setStartMonth] = useState(1);
  const [themeId, setThemeId] = useState<CalendarThemeId>('clean_gallery');

  const selectedProduct = useMemo(() => products.find((product) => product.id === productId) ?? products[0], [productId]);
  const selectedTheme = useMemo(() => themes.find((theme) => theme.id === themeId) ?? themes[0], [themeId]);
  const selectedPeriod = useMemo(() => periodModes.find((period) => period.id === periodMode) ?? periodModes[0], [periodMode]);
  const effectiveStartMonth = periodMode === 'calendar_year' ? 1 : periodMode === 'school_year' ? 9 : startMonth;
  const effectiveYear = periodMode === 'calendar_year' ? 2026 : year;

  const goNext = () => setCurrentStep((step) => Math.min(step + 1, stepTitles.length - 1));
  const goBack = () => setCurrentStep((step) => Math.max(step - 1, 0));

  const handleCreateProject = () => {
    const project = createCalendarProject({
      productId,
      periodMode,
      year: effectiveYear,
      startMonth: effectiveStartMonth,
      themeId,
    });

    saveProject(project);
    window.location.assign('/upload');
  };

  return (
    <main className="start-shell">
      <div className="start-card">
        <header className="start-header">
          <Logo />
          <div className="start-header__links">
            <a href="/projects" className="start-header__link">Otevřít rozpracovaný projekt</a>
            <a href="/" className="start-header__link">Zpět na homepage</a>
          </div>
        </header>

        <section className="start-hero" aria-labelledby="start-title">
          <p className="eyebrow">Start Flow v0.1</p>
          <h1 id="start-title">Začni tvořit kalendář</h1>
          <p>Vyber produkt, období a vizuální styl. Projekt uložíme lokálně a připravíme další krok nahrání fotek.</p>
        </section>

        <nav className="start-stepper" aria-label="Kroky tvorby kalendáře">
          {stepTitles.map((title, index) => (
            <button
              key={title}
              type="button"
              className={index === currentStep ? 'start-stepper__item is-active' : 'start-stepper__item'}
              onClick={() => setCurrentStep(index)}
              aria-current={index === currentStep ? 'step' : undefined}
            >
              <span>0{index + 1}</span>
              {title}
            </button>
          ))}
        </nav>

        <section className="start-panel">
          {currentStep === 0 && (
            <div>
              <div className="start-section-heading">
                <h2>Vyber typ kalendáře</h2>
                <p>Pouze 3 MVP produkty připravené pro první verzi Calendar Studio.</p>
              </div>
              <div className="start-product-grid">
                {products.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    className={product.id === productId ? 'start-product-card is-active' : 'start-product-card'}
                    onClick={() => setProductId(product.id)}
                  >
                    <picture>
                      <source type="image/webp" srcSet={productImagePath(product.image, 'webp')} />
                      <img src={productImagePath(product.image, 'jpg')} alt={product.title} />
                    </picture>
                    <span className="start-badge">{product.badge}</span>
                    <h3>{product.title}</h3>
                    <p>{product.description}</p>
                    <dl>
                      <div><dt>Formát</dt><dd>{product.format}</dd></div>
                      <div><dt>Pro koho</dt><dd>{product.audience}</dd></div>
                    </dl>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <div className="start-section-heading">
                <h2>Vyber období</h2>
                <p>Kalendář má vždy 12 měsíců. České svátky a jmeniny připravíme pro další krok.</p>
              </div>
              <div className="start-option-grid">
                {periodModes.map((period) => (
                  <button
                    key={period.id}
                    type="button"
                    className={period.id === periodMode ? 'start-option-card is-active' : 'start-option-card'}
                    onClick={() => setPeriodMode(period.id)}
                  >
                    <strong>{period.title}</strong>
                    <span>{period.description}</span>
                  </button>
                ))}
              </div>
              {(periodMode === 'custom_start' || periodMode === 'anniversary_year') && (
                <div className="start-fields" aria-label="Vlastní začátek období">
                  <label>
                    Začínající měsíc
                    <select value={startMonth} onChange={(event) => setStartMonth(Number(event.target.value))}>
                      {months.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}
                    </select>
                  </label>
                  <label>
                    Rok začátku
                    <input type="number" min="2026" max="2035" value={year} onChange={(event) => setYear(Number(event.target.value))} />
                  </label>
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <div className="start-section-heading">
                <h2>Vyber styl</h2>
                <p>Pro MVP volíme čisté a předvídatelné styly připravené pro editor.</p>
              </div>
              <div className="start-theme-grid">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    type="button"
                    className={theme.id === themeId ? `start-theme-card start-theme-card--${theme.id} is-active` : `start-theme-card start-theme-card--${theme.id}`}
                    onClick={() => setThemeId(theme.id)}
                  >
                    <span aria-hidden="true" />
                    <strong>{theme.title}</strong>
                    <small>{theme.description}</small>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <div className="start-section-heading">
                <h2>Shrnutí a pokračování</h2>
                <p>Zkontroluj nastavení. Návrh uložíme lokálně do tohoto prohlížeče.</p>
              </div>
              <div className="start-summary">
                <div><span>Produkt</span><strong>{selectedProduct.title}</strong></div>
                <div><span>Období</span><strong>{selectedPeriod.title}</strong><small>{months[effectiveStartMonth - 1]} {effectiveYear}, 12 měsíců</small></div>
                <div><span>Styl</span><strong>{selectedTheme.title}</strong></div>
                <div><span>Jazyk</span><strong>CZ</strong></div>
                <div><span>Kalendářní data</span><strong>české svátky + jmeniny připravené pro další krok</strong></div>
              </div>
            </div>
          )}
        </section>

        <footer className="start-actions">
          <button type="button" className="start-back" onClick={goBack} disabled={currentStep === 0}>Zpět</button>
          <div>
            <Button href="/" variant="secondary">Zpět na homepage</Button>
            {currentStep < 3 ? (
              <button type="button" className="start-primary" onClick={goNext}>Pokračovat</button>
            ) : (
              <button type="button" className="start-primary" onClick={handleCreateProject}>Pokračovat k nahrání fotek</button>
            )}
          </div>
        </footer>
      </div>
    </main>
  );
}
